/**
 * AdminReconciliationDashboard.jsx
 * =================================
 * Sprint R4 — Webhook Reconciliation Admin Dashboard (super_admin only).
 *
 * Surfaces:
 *  - Metrics KPIs (events by status, stuck transactions, credit count)
 *  - Failed webhook events
 *  - Stuck `paid` transactions (paid but uncredited)
 *  - Recent gateway events (Stripe + Razorpay)
 *  - Manual reconcile actions (Force-credit / Mark-failed) with audit
 *    reason capture.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Activity,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Filter,
  Clock,
  ShieldCheck,
  X,
} from "lucide-react";

const TABS = [
  { key: "metrics", label: "Metrics", icon: Activity },
  { key: "classification", label: "Classification", icon: ShieldCheck },
  { key: "bulk", label: "Bulk Reconcile", icon: CheckCircle2 },
  { key: "review", label: "Manual Review", icon: AlertTriangle },
  { key: "failures", label: "Failed Events", icon: AlertTriangle },
  { key: "stuck", label: "Stuck Transactions", icon: Clock },
  { key: "events", label: "Recent Events", icon: ShieldCheck },
];

const CLASSIFICATION_LABELS = {
  paid_uncredited_eligible:       { label: "Safe to credit",       tone: "emerald" },
  already_credited_status_drift:  { label: "Status drift",         tone: "blue" },
  wallet_missing:                 { label: "Wallet missing",       tone: "amber" },
  entity_mismatch:                { label: "Entity mismatch",      tone: "rose" },
  amount_mismatch:                { label: "Amount mismatch",      tone: "rose" },
  currency_mismatch:              { label: "Currency mismatch",    tone: "rose" },
  duplicate_detected:             { label: "Duplicate",            tone: "amber" },
  chargeback_or_reversal_review:  { label: "Chargeback / Refund",  tone: "rose" },
  suspected_test_noise:           { label: "Test noise",           tone: "zinc" },
  manual_review_required:         { label: "Manual review",        tone: "amber" },
  gateway_status_unclear:         { label: "Gateway unclear",      tone: "zinc" },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-300",
  blue:    "bg-blue-100 text-blue-700 border-blue-300",
  amber:   "bg-amber-100 text-amber-700 border-amber-300",
  rose:    "bg-rose-100 text-rose-700 border-rose-300",
  zinc:    "bg-zinc-100 text-zinc-600 border-zinc-300",
};

const STATUS_COLOURS = {
  received: "bg-slate-100 text-slate-700 border-slate-300",
  processing: "bg-blue-100 text-blue-700 border-blue-300",
  processed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  ignored: "bg-zinc-100 text-zinc-600 border-zinc-300",
  failed: "bg-rose-100 text-rose-700 border-rose-300",
  duplicate: "bg-amber-100 text-amber-700 border-amber-300",
};

function StatusPill({ status }) {
  const cls = STATUS_COLOURS[status] || "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <span
      data-testid={`recon-status-pill-${status || "unknown"}`}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {status || "—"}
    </span>
  );
}

function KpiCard({ label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-navy-100 bg-white px-5 py-4 shadow-sm"
    >
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-semibold text-navy-900">{value ?? "—"}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function BucketCard({ title, rows, testid, labelMap = null }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</h4>
      <div className="mt-2 space-y-1.5">
        {entries.length === 0 ? (
          <div className="text-xs text-slate-400">No data.</div>
        ) : (
          entries.map(([k, v]) => {
            const label = labelMap?.[k]?.label || k.replaceAll("_", " ");
            const tone = labelMap?.[k]?.tone || null;
            return (
              <div key={k} className="flex items-center justify-between text-xs">
                <span className={`capitalize text-slate-700${tone ? ` px-2 py-0.5 rounded-full border ${TONE_CLASSES[tone]}` : ""}`}>
                  {label}
                </span>
                <span className="font-semibold text-slate-900">{v}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function ForceCreditModal({ open, txn, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [action, setAction] = useState("paid");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setAction("paid");
    }
  }, [open]);

  if (!open || !txn) return null;

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for the audit log.");
      return;
    }
    setBusy(true);
    try {
      await onConfirm({ transactionId: txn.id, action, reason });
      setBusy(false);
      onClose();
    } catch {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="recon-force-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-navy-900">
              Reconcile transaction
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              <span className="font-mono">{txn.id}</span> · status{" "}
              <span className="font-semibold">{txn.status}</span> ·{" "}
              {txn.currency} {txn.amount}
            </p>
          </div>
          <button
            data-testid="recon-force-modal-close"
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <label className="block text-xs font-medium text-slate-600">
            Action
          </label>
          <div className="flex gap-2">
            <button
              data-testid="recon-force-action-paid"
              onClick={() => setAction("paid")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm ${
                action === "paid"
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-white text-slate-600"
              }`}
            >
              Force credit (mark paid → credited)
            </button>
            <button
              data-testid="recon-force-action-failed"
              onClick={() => setAction("failed")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm ${
                action === "failed"
                  ? "border-rose-500 bg-rose-50 text-rose-700"
                  : "border-slate-200 bg-white text-slate-600"
              }`}
            >
              Mark failed
            </button>
          </div>

          <label
            htmlFor="recon-reason"
            className="block pt-2 text-xs font-medium text-slate-600"
          >
            Audit reason (required)
          </label>
          <textarea
            id="recon-reason"
            data-testid="recon-force-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Verified payment in Razorpay dashboard, payment id pay_XYZ"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-navy-500 focus:outline-none focus:ring-1 focus:ring-navy-500"
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            data-testid="recon-force-cancel"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            data-testid="recon-force-submit"
            disabled={busy}
            onClick={submit}
            className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-white hover:bg-navy-800 disabled:opacity-60"
          >
            {busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminReconciliationDashboard() {
  const [tab, setTab] = useState("metrics");
  const [metrics, setMetrics] = useState(null);
  const [failures, setFailures] = useState([]);
  const [stuck, setStuck] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [statusFilter, setStatusFilter] = useState("");
  const [gatewayFilter, setGatewayFilter] = useState("");
  const [modalTxn, setModalTxn] = useState(null);
  // R4B state
  const [classification, setClassification] = useState(null);
  const [alertCounters, setAlertCounters] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [manualReviews, setManualReviews] = useState([]);
  const [manualReviewFilter, setManualReviewFilter] = useState("open");

  const loadMetrics = useCallback(async () => {
    try {
      const r = await api.get(`/admin/diploks/reconciliation/metrics?days=${days}`);
      setMetrics(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [days]);

  const loadAlertCounters = useCallback(async () => {
    try {
      const r = await api.get(`/admin/diploks/reconciliation/alert-counters`);
      setAlertCounters(r.data);
    } catch (e) {
      // silent — counters are non-critical
    }
  }, []);

  const loadClassification = useCallback(async () => {
    try {
      const r = await api.get(`/admin/diploks/reconciliation/stuck-classification?older_than_minutes=15&limit=2000`);
      setClassification(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadManualReviews = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (manualReviewFilter) params.set("status", manualReviewFilter);
      const r = await api.get(`/admin/diploks/reconciliation/manual-review?${params}`);
      setManualReviews(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [manualReviewFilter]);

  const loadFailures = useCallback(async () => {
    try {
      const r = await api.get(`/admin/diploks/reconciliation/failures?days=${days}&limit=200`);
      setFailures(r.data?.failures || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [days]);

  const loadStuck = useCallback(async () => {
    try {
      const r = await api.get(`/admin/diploks/reconciliation/stuck?older_than_minutes=15&limit=200`);
      setStuck(r.data?.transactions || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (statusFilter) params.set("status", statusFilter);
      if (gatewayFilter) params.set("gateway", gatewayFilter);
      const r = await api.get(`/admin/diploks/reconciliation/events?${params}`);
      setEvents(r.data?.events || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [statusFilter, gatewayFilter]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadMetrics(), loadAlertCounters(), loadClassification(),
      loadManualReviews(), loadFailures(), loadStuck(), loadEvents(),
    ]);
    setLoading(false);
  }, [loadMetrics, loadAlertCounters, loadClassification, loadManualReviews,
       loadFailures, loadStuck, loadEvents]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const onBulkPreview = async () => {
    try {
      const r = await api.post(`/admin/diploks/reconciliation/bulk-preview`, {
        older_than_minutes: 15, limit: 5000,
      });
      setBulkPreview(r.data);
      toast.success(`Preview ready: ${r.data?.eligible_count || 0} eligible`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onBulkApply = async () => {
    if (bulkReason.trim().length < 8) {
      toast.error("Please provide an audit reason (min 8 characters).");
      return;
    }
    if (!bulkConfirm) {
      toast.error("Please tick the eligibility confirmation checkbox.");
      return;
    }
    setBulkApplying(true);
    try {
      const r = await api.post(`/admin/diploks/reconciliation/bulk-apply`, {
        reason: bulkReason,
        confirm_eligible_checkbox: bulkConfirm,
        older_than_minutes: 15, limit: 5000,
      });
      const d = r.data || {};
      toast.success(
        `Bulk job ${d.job_id}: credited ${d.credited_count}, already ${d.already_credited_count}, errors ${d.error_count}`,
      );
      setBulkReason("");
      setBulkConfirm(false);
      setBulkPreview(null);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkApplying(false);
    }
  };

  const onRefreshManualReview = async () => {
    try {
      const r = await api.post(`/admin/diploks/reconciliation/refresh-manual-review?older_than_minutes=15`);
      toast.success(`Refreshed — ${r.data?.upserted || 0} review row(s) upserted`);
      await loadManualReviews();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onPatchReview = async (id, patch) => {
    try {
      await api.patch(`/admin/diploks/reconciliation/manual-review/${encodeURIComponent(id)}`, patch);
      toast.success("Review updated.");
      await loadManualReviews();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onForceConfirm = async ({ transactionId, action, reason }) => {
    try {
      const params = new URLSearchParams({ gateway_status: action });
      const r = await api.post(
        `/admin/diploks/reconcile/payment/${encodeURIComponent(transactionId)}?${params}`,
        { reason },
      );
      const data = r.data || {};
      if (data.credited) {
        toast.success(`Credited ${data.diploks_credited || ""} Diploks`);
      } else if (data.already_credited) {
        toast.success("Already credited (no-op).");
      } else if (data.action === "marked_failed") {
        toast.success("Transaction marked failed.");
      } else if (data.action === "still_pending") {
        toast.info("Transaction still pending — no action taken.");
      } else {
        toast.success("Reconciliation completed.");
      }
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  const eventStatusCounts = useMemo(() => {
    const c = metrics?.events_by_status || {};
    return {
      processed: c.processed || 0,
      failed: c.failed || 0,
      duplicate: c.duplicate || 0,
      ignored: c.ignored || 0,
      received: c.received || 0,
    };
  }, [metrics]);

  return (
    <DashboardLayout title="Webhook Reconciliation">
      <div data-testid="recon-dashboard" className="space-y-6">
        {/* Header bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy-100 bg-white px-5 py-4 shadow-sm">
          <div>
            <h1 className="text-xl font-semibold text-navy-900">
              Webhook Reconciliation
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Revenue safety surface — duplicate webhooks deduped, stuck
              transactions credited safely, no double-charges.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label
              htmlFor="recon-days"
              className="text-xs font-medium text-slate-600"
            >
              Window
            </label>
            <select
              id="recon-days"
              data-testid="recon-days-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-md border border-slate-300 px-2 py-1 text-sm"
            >
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
            <button
              data-testid="recon-refresh"
              onClick={refreshAll}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`recon-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  active
                    ? "border-navy-700 bg-navy-900 text-white shadow"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Metrics */}
        {tab === "metrics" ? (
          <div
            data-testid="recon-metrics-panel"
            className="space-y-4"
          >
            {alertCounters && alertCounters.total_attention_needed > 0 ? (
              <div
                data-testid="recon-alert-banner"
                className="rounded-2xl border border-amber-300 bg-amber-50 px-5 py-3 text-sm text-amber-900 shadow-sm"
              >
                <strong>{alertCounters.total_attention_needed}</strong> payment events need reconciliation review —{" "}
                {alertCounters.stuck_paid_transactions} stuck,{" "}
                {alertCounters.failed_webhook_events} failed,{" "}
                {alertCounters.manual_reviews_open} in manual review,{" "}
                {alertCounters.refund_pending_transactions} refund-pending.
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
              <KpiCard
                testid="recon-kpi-processed"
                label="Processed"
                value={eventStatusCounts.processed}
                hint={`Last ${days}d`}
              />
              <KpiCard
                testid="recon-kpi-failed"
                label="Failed"
                value={eventStatusCounts.failed}
                hint="Need attention"
              />
              <KpiCard
                testid="recon-kpi-duplicate"
                label="Duplicates Deduped"
                value={eventStatusCounts.duplicate}
                hint="Safely ignored"
              />
              <KpiCard
                testid="recon-kpi-stuck"
                label="Stuck (paid > 15m)"
                value={metrics?.stuck_paid_transactions ?? 0}
                hint="Probable missed webhook"
              />
              <KpiCard
                testid="recon-kpi-credits"
                label="Credits Written"
                value={metrics?.credit_events ?? 0}
                hint="diploks_credit_events"
              />
            </div>
          </div>
        ) : null}

        {/* Classification — R4B */}
        {tab === "classification" ? (
          <div data-testid="recon-classification-panel" className="space-y-4">
            <div className="rounded-2xl border border-navy-100 bg-white px-5 py-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-navy-900">
                    Classification of {classification?.total ?? "…"} stuck transactions
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {classification?.total_diploks_to_credit_if_bulk_applied ?? 0} Diploks would be credited if all eligible rows were applied.
                  </p>
                </div>
                <button
                  data-testid="recon-classification-refresh"
                  onClick={loadClassification}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw size={12} className="mr-1 inline" />
                  Re-classify
                </button>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                <BucketCard title="By classification" rows={classification?.by_classification} testid="recon-cls-by-classification" labelMap={CLASSIFICATION_LABELS} />
                <BucketCard title="By gateway"        rows={classification?.by_gateway} testid="recon-cls-by-gateway" />
                <BucketCard title="By currency"       rows={classification?.by_currency} testid="recon-cls-by-currency" />
                <BucketCard title="By billing entity" rows={classification?.by_entity} testid="recon-cls-by-entity" />
                <BucketCard title="By wallet type"    rows={classification?.by_wallet_type} testid="recon-cls-by-wallet" />
                <BucketCard title="By age"            rows={classification?.by_age_bucket} testid="recon-cls-by-age" />
              </div>
            </div>
          </div>
        ) : null}

        {/* Bulk Reconcile — R4B */}
        {tab === "bulk" ? (
          <div data-testid="recon-bulk-panel" className="space-y-4">
            <div className="rounded-2xl border border-navy-100 bg-white px-5 py-4 shadow-sm">
              <h3 className="text-sm font-semibold text-navy-900">
                Bulk reconciliation — Safe to credit only
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Credits ONLY the <strong>paid_uncredited_eligible</strong> bucket via the idempotent service.
                Risky transactions are excluded and held for manual review.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  data-testid="recon-bulk-preview-btn"
                  onClick={onBulkPreview}
                  className="rounded-lg border border-navy-200 bg-navy-50 px-3 py-1.5 text-sm font-medium text-navy-700 hover:bg-navy-100"
                >
                  Run preview
                </button>
              </div>

              {bulkPreview ? (
                <div data-testid="recon-bulk-preview-result" className="mt-5 space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <KpiCard testid="recon-bulk-kpi-total"     label="Scanned"               value={bulkPreview.preview_count} />
                    <KpiCard testid="recon-bulk-kpi-eligible"  label="Eligible to credit"    value={bulkPreview.eligible_count} />
                    <KpiCard testid="recon-bulk-kpi-excluded"  label="Excluded (risky)"      value={bulkPreview.excluded_count} />
                    <KpiCard testid="recon-bulk-kpi-diploks"   label="Diploks to be credited" value={bulkPreview.total_diploks_to_credit} />
                  </div>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <BucketCard title="Eligible by gateway"  rows={bulkPreview.split_by_gateway} testid="recon-bulk-by-gateway" />
                    <BucketCard title="Eligible by currency" rows={bulkPreview.split_by_currency} testid="recon-bulk-by-currency" />
                    <BucketCard title="Eligible by entity"   rows={bulkPreview.split_by_entity} testid="recon-bulk-by-entity" />
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Excluded summary (held for manual review)
                    </h4>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
                      {Object.entries(bulkPreview.excluded_summary || {}).map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between rounded-md bg-white px-2 py-1.5 border border-slate-200">
                          <span className="capitalize text-slate-600">{CLASSIFICATION_LABELS[k]?.label || k.replaceAll("_", " ")}</span>
                          <span className="font-semibold text-slate-900">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <label htmlFor="recon-bulk-reason" className="block text-xs font-medium text-emerald-900">
                      Audit reason (required, min 8 chars)
                    </label>
                    <textarea
                      id="recon-bulk-reason"
                      data-testid="recon-bulk-reason"
                      rows={2}
                      value={bulkReason}
                      onChange={(e) => setBulkReason(e.target.value)}
                      placeholder="e.g. Backfilled R4 reconciliation for the 71 paid_uncredited_eligible txns surfaced post-R4 launch."
                      className="w-full rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                    <label className="flex cursor-pointer items-start gap-2 text-xs text-emerald-900">
                      <input
                        type="checkbox"
                        data-testid="recon-bulk-confirm"
                        checked={bulkConfirm}
                        onChange={(e) => setBulkConfirm(e.target.checked)}
                        className="mt-0.5"
                      />
                      <span>
                        I confirm these transactions are paid, valid, not
                        already credited, and eligible for Diploks crediting.
                      </span>
                    </label>
                    <button
                      data-testid="recon-bulk-apply-btn"
                      disabled={bulkApplying || !bulkConfirm || bulkReason.trim().length < 8}
                      onClick={onBulkApply}
                      className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-emerald-300"
                    >
                      {bulkApplying ? "Applying…" : `Credit ${bulkPreview.eligible_count} transactions`}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Manual Review — R4B */}
        {tab === "review" ? (
          <div data-testid="recon-review-panel" className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-navy-100 bg-white px-5 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <label htmlFor="recon-review-status" className="text-xs font-medium text-slate-600">
                  Status
                </label>
                <select
                  id="recon-review-status"
                  data-testid="recon-review-status"
                  value={manualReviewFilter}
                  onChange={(e) => setManualReviewFilter(e.target.value)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="archived">Archived</option>
                  <option value="">All</option>
                </select>
              </div>
              <button
                data-testid="recon-review-refresh-queue"
                onClick={onRefreshManualReview}
                className="rounded-md border border-navy-200 bg-navy-50 px-3 py-1.5 text-xs font-medium text-navy-700 hover:bg-navy-100"
              >
                Refresh manual-review queue from classifier
              </button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left">Created</th>
                      <th className="px-4 py-2 text-left">Txn</th>
                      <th className="px-4 py-2 text-left">Classification</th>
                      <th className="px-4 py-2 text-left">Reason</th>
                      <th className="px-4 py-2 text-left">Notes</th>
                      <th className="px-4 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {manualReviews.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                          No manual reviews matching the filter.
                        </td>
                      </tr>
                    ) : (
                      manualReviews.map((m) => {
                        const tone = CLASSIFICATION_LABELS[m.classification]?.tone || "zinc";
                        return (
                          <tr key={m.id} data-testid={`recon-review-row-${m.id}`}>
                            <td className="px-4 py-2 font-mono text-xs text-slate-600">
                              {m.created_at?.slice(0, 19).replace("T", " ")}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs">{m.transaction_id}</td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${TONE_CLASSES[tone]}`}>
                                {CLASSIFICATION_LABELS[m.classification]?.label || m.classification}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-slate-700 max-w-md">
                              {m.detected_reason}
                            </td>
                            <td className="px-4 py-2 text-xs">
                              <input
                                data-testid={`recon-review-notes-${m.id}`}
                                defaultValue={m.admin_notes || ""}
                                onBlur={(e) => {
                                  if (e.target.value !== (m.admin_notes || "")) {
                                    onPatchReview(m.id, { admin_notes: e.target.value });
                                  }
                                }}
                                placeholder="Add notes…"
                                className="w-44 rounded border border-slate-300 px-2 py-1 text-xs"
                              />
                            </td>
                            <td className="px-4 py-2 text-xs">
                              <select
                                data-testid={`recon-review-status-${m.id}`}
                                value={m.status}
                                onChange={(e) => onPatchReview(m.id, { status: e.target.value })}
                                className="rounded border border-slate-300 px-2 py-1 text-xs"
                              >
                                <option value="open">Open</option>
                                <option value="in_progress">In progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="archived">Archived</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}

        {/* Failures */}
        {tab === "failures" ? (
          <div
            data-testid="recon-failures-panel"
            className="overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm"
          >
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-medium text-slate-700">
              {failures.length} failed gateway event(s) in the last {days} days
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">When</th>
                    <th className="px-4 py-2 text-left">Gateway</th>
                    <th className="px-4 py-2 text-left">Event</th>
                    <th className="px-4 py-2 text-left">Linked Txn</th>
                    <th className="px-4 py-2 text-left">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {failures.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-sm text-slate-500"
                      >
                        No failures recorded — clean window.
                      </td>
                    </tr>
                  ) : (
                    failures.map((f) => (
                      <tr key={f.id} data-testid={`recon-failure-row-${f.id}`}>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">
                          {f.created_at?.slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="px-4 py-2 capitalize">{f.gateway_name}</td>
                        <td className="px-4 py-2 font-mono text-xs">{f.event_type}</td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {f.linked_transaction_id || "—"}
                        </td>
                        <td className="px-4 py-2 text-rose-700">
                          {f.error_message || "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* Stuck */}
        {tab === "stuck" ? (
          <div
            data-testid="recon-stuck-panel"
            className="overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm"
          >
            <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-medium text-slate-700">
              {stuck.length} transaction(s) paid but uncredited (older than 15
              minutes)
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Updated</th>
                    <th className="px-4 py-2 text-left">Txn</th>
                    <th className="px-4 py-2 text-left">Kind</th>
                    <th className="px-4 py-2 text-left">Gateway</th>
                    <th className="px-4 py-2 text-left">Entity</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stuck.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-sm text-slate-500"
                      >
                        No stuck transactions. Webhooks are clean.
                      </td>
                    </tr>
                  ) : (
                    stuck.map((s) => (
                      <tr key={s.id} data-testid={`recon-stuck-row-${s.id}`}>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">
                          {s.updated_at?.slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{s.id}</td>
                        <td className="px-4 py-2">{s.kind || "—"}</td>
                        <td className="px-4 py-2 capitalize">{s.gateway || "—"}</td>
                        <td className="px-4 py-2 capitalize">
                          {s.billing_entity || "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {s.currency} {s.amount ?? "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            data-testid={`recon-stuck-reconcile-${s.id}`}
                            onClick={() => setModalTxn(s)}
                            className="inline-flex items-center gap-1 rounded-md border border-navy-200 bg-navy-50 px-3 py-1 text-xs font-medium text-navy-700 hover:bg-navy-100"
                          >
                            <CheckCircle2 size={12} />
                            Reconcile
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* Events */}
        {tab === "events" ? (
          <div
            data-testid="recon-events-panel"
            className="space-y-3 overflow-hidden rounded-2xl border border-navy-100 bg-white shadow-sm"
          >
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm">
              <Filter size={14} className="text-slate-500" />
              <select
                data-testid="recon-events-gateway"
                value={gatewayFilter}
                onChange={(e) => setGatewayFilter(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs"
              >
                <option value="">All gateways</option>
                <option value="razorpay">Razorpay</option>
                <option value="stripe">Stripe</option>
              </select>
              <select
                data-testid="recon-events-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs"
              >
                <option value="">All statuses</option>
                <option value="received">Received</option>
                <option value="processed">Processed</option>
                <option value="ignored">Ignored</option>
                <option value="failed">Failed</option>
                <option value="duplicate">Duplicate</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">When</th>
                    <th className="px-4 py-2 text-left">Gateway</th>
                    <th className="px-4 py-2 text-left">Event</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Linked Txn</th>
                    <th className="px-4 py-2 text-left">Idem Key</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {events.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-sm text-slate-500"
                      >
                        No events to display in the current filter.
                      </td>
                    </tr>
                  ) : (
                    events.map((ev) => (
                      <tr key={ev.id} data-testid={`recon-event-row-${ev.id}`}>
                        <td className="px-4 py-2 font-mono text-xs text-slate-600">
                          {ev.created_at?.slice(0, 19).replace("T", " ")}
                        </td>
                        <td className="px-4 py-2 capitalize">{ev.gateway_name}</td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {ev.event_type}
                        </td>
                        <td className="px-4 py-2">
                          <StatusPill status={ev.status} />
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {ev.linked_transaction_id || "—"}
                        </td>
                        <td className="px-4 py-2 font-mono text-[10px] text-slate-500">
                          {ev.idempotency_key}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {/* Force-credit modal */}
        <ForceCreditModal
          open={!!modalTxn}
          txn={modalTxn}
          onClose={() => setModalTxn(null)}
          onConfirm={onForceConfirm}
        />
      </div>
    </DashboardLayout>
  );
}
